import { CountryCode } from "@features/country/domain";
import type { LocalizedValue } from "@features/shared/domain";

export enum ChallengeStatus {
  OFFERED = "OFFERED",
  ACCEPTED = "ACCEPTED",
  DECLINED = "DECLINED",
  IN_PROGRESS = "IN_PROGRESS",
  IN_PROGRESS_EXTRA_TIME = "IN_PROGRESS_EXTRA_TIME",
  ACHIEVED = "ACHIEVED",
  NOT_ACHIEVED = "NOT_ACHIEVED",
  EXPIRED = "EXPIRED",
  COMPLETED = "COMPLETED",
}

export enum ChallengeType {
  SALES = "SALES",
  INVENTORY = "INVENTORY",
  CUSTOMER_SATISFACTION = "CUSTOMER_SATISFACTION",
  PRODUCT_QUALITY = "PRODUCT_QUALITY",
}

export enum ChallengeOrderType {
  RECEIVED = "order_received",
  CONFIRMED = "confirmed",
  DELIVERED = "delivered",
  PREPAID = "prepaid",
}

export enum BucketType {
  ORDER_TYPE_ONLY = "ORDER_TYPE_ONLY",
  PRE_PAID = "PRE_PAID",
  PRODUCT_BASED = "PRODUCT_BASED",
}

export enum RewardsStatus {
  PENDING = "PENDING",
  PAID = "PAID",
  CANCELLED = "CANCELLED",
}

export type DynamicIncentiveChallenge = {
  title: string;
  description: string;
  type: ChallengeType;
  status: ChallengeStatus;
  startDate: Date;
  endDate: Date;
  target: number;
  currentProgress: number;
  reward: number;
  feedback?: string;
  completedAt?: Date;
  acceptedAt?: Date;
  declinedAt?: Date;
};

export type DynamicIncentiveChallengeEntity = {
  name: LocalizedValue;
  title: string;
  result?: ChallengeResultEntity;
  country: CountryCode;
  description?: LocalizedValue;
  termsAndConditions: LocalizedValue[];
  levels: ChallengeLevelsData[];
  type?: "capped" | "uncapped";
  status: ChallengeStatus;
  startDate?: Date;
  endDate?: Date;
  expiryDate?: Date;
  extraTime?: Date;
  id: string;
  bucketType: BucketType | string;
  sku?: string;
  productId?: string;
  orders: {
    count?: number;
    max: number;
    type: ChallengeOrderType;
    progressResult?: ChallengeOrderProgressData;
    numberOfOrders: number;
  };
  // Grouped properties
  state: {
    isOffered: boolean;
    isInProgress: boolean;
    isCompleted: boolean;
    isAchieved: boolean;
    isNotAchieved: boolean;
    isExpired: boolean;
    challengeTimeIsUp: boolean;
    shouldDisplayProgressBar: boolean;
    rewardsNotEqualToBaseRewards: boolean;
    challengeInProgress: boolean;
    challengeStillHasTimeToEnd: boolean;
    hasBaseRewards: boolean;
    canSubmitFeedback: boolean;
  };
  progress: {
    percentage: number;
    value: number;
    target: number;
    loading: number;
    numberOfAdditionalOrders: number;
    totalRewardsForExtraOrders: number;
  };
  time: {
    durationInDays: number;
    durationInHours: number;
    remainingInHours?: number;
    isUp: boolean;
    hasRemaining: boolean;
    extraTimeInDays?: number;
    acceptedDate?: string;
  };
  rewards: {
    hasAdditional: boolean;
    hasStandard: boolean;
    hasWon: boolean;
    max: number;
    base: number;
    additional: number;
    difference: number; // between base and additional rewards
  };
  carnival: {
    exists: boolean;
    hasWon: boolean;
    shouldDisplayDetails: boolean;
    didNotWin: boolean;
    rewardPerExtraOrder: number;
    achievedOrders: number;
    inProgress: boolean;
  };
};

export type ChallengeOrderProgressData = {
  totalPlacedOrders: number;
  confirmedOrders: number;
  deliveredOrders: number;
  pendingConfirmation: number;
  pendingDelivery: number;
  prePaidOrdersCount: number;
};

export type ChallengeLevelsData = {
  level: number;
  numOfOrders: number;
  rewardPerOrder: number;
  rewardPerOrderExceedingLevelCap?: number;
};

export enum FeedbackRate {
  SATISFIED = "SATISFIED",
  PARTIALLY_SATISFIED = "PARTIALLY_SATISFIED",
  NOT_SATISFIED = "NOT_SATISFIED",
}

export type DynamicIncentiveFeedback = {
  feedback?: string;
  rate: FeedbackRate;
  id: string;
};

export type ChallengeResultEntity = {
  numOfAchievedOrders: number;
  rewards: number;
  rewardsStatus: RewardsStatus | string;
  createdAt: Date;
  additionalAchievedOrders?: number;
  baseRewards?: number;
};

export type HistoryDynamicIncentiveProgramEntity = {
  challengeReference: number;
  challengeName: LocalizedValue;
  additionalOrdersAchieved?: number;
  startedAt: Date;
  endedAt: Date;
  rewardsStatus: RewardsStatus | string;
  rewardsValue: number;
  challengeFeedbackRate: FeedbackRate;
  achievedOrders: number;
  maxTargetOrdersCount: number;
  orderType: ChallengeOrderType;
};
